import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import apiPut from 'lib/network/apiPut'
import _ from 'lodash'
import Switch from 'components/atoms/Switch'

const Discount = ({ onSuccess, close, id = null }) => {
  const { t } = useTranslation()
  const [type, setType] = useState('')
  const [companyNames, setCompanyNames] = useState([])
  const [selectedCompany, setSelectedCompany] = useState([])

  const [formData, setFormData] = useState({
    cupon: '',
    amount: '',
    percentage: '',
    effectiveStartDate: '',
    effectiveEndDate: '',
    isActive: false,
    discountValid: '',
  })

  useEffect(() => {
    if (id) {
      fetchDiscountData()
    }
  }, [id])

  const fetchDiscountData = async () => {
    const response = await apiGet(`/discount/${id}`)
    if (response.success) {
      const data = response.data
      setFormData({
        cupon: data.cupon || '',
        amount: data.amount || '',
        percentage: data.percentage || '',
        effectiveStartDate: formatDate(data.effectiveStartDate) || '',
        effectiveEndDate: formatDate(data.effectiveEndDate) || '',
        isActive: data.status || false,
        discountValid: data.discountValid || '',
      })

      setType(data.type || '')
      const companies = data.company
      let companyIds = []
      if (companies) {
        if (_.isArray(companies)) {
          companyIds = companies.map(company => (_.isObject(company) && company._id ? company._id : company))
        } else if (_.isObject(companies) && companies._id) {
          companyIds = [companies._id]
        } else {
          companyIds = [companies]
        }
      }
      setSelectedCompany(companyIds)
    } else {
      notify.error(t('discountModal.messages.fetchFailed'))
    }
  }
  const formatDate = isoString => {
    return isoString ? _.split(isoString, 'T')[0] : ''
  }

  const handleChange = e => {
    const { name, value, type, checked } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })

    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
  }
  const handleSaveDiscount = async () => {
    const { cupon, amount, percentage, effectiveStartDate, effectiveEndDate, discountValid } = formData
    const payload = { type, cupon, amount, percentage, effectiveStartDate, effectiveEndDate, status: formData.isActive, discountValid, company: selectedCompany }

    const endpoint = id ? `discount/${id}` : 'add/discount'
    const apiMethod = id ? apiPut : apiPost
    const response = await apiMethod(endpoint, payload)

    if (response.success) {
      const message = id ? t('discountModal.messages.discountUpdated') : t('discountModal.messages.discountAdded')
      notify.success(message)
      onSuccess(response.data._id)
    } else {
      notify.error(response.message || t('discountModal.messages.saveFailed'))
    }
  }

  return (
    <div className="space-y-6 sm:space-y-5">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <label htmlFor="type" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.discountType')}
          </label>
          <Selection
            onChange={value => {
              if (value && value.id) {
                setType(prevValue => (prevValue === value.id ? '' : value.id))
              }
            }}
            title={t('discountModal.form.selectDiscountType')}
            list={[
              { id: 'Flat', name: t('discountModal.types.flat') },
              { id: 'Percentage', name: t('discountModal.types.percentage') },
            ]}
            value={type}
            className="w-full"
          />
        </div>

        <div>
          <label htmlFor="cupon" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.cuponCode')}
          </label>
          <input type="text" name="cupon" value={formData.cupon} onChange={handleChange} className="w-full" />
        </div>
        <div>
          <label htmlFor="fuelPolicy" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.company')}
          </label>
          <Selection
            onChange={ids => setSelectedCompany(ids || [])}
            title={t('discountModal.form.selectCompany')}
            list={companyNames}
            value={selectedCompany}
            multiple={true}
            className="w-full"
          />
        </div>

        {type === 'Flat' && (
          <div>
            <label htmlFor="amount" className="block text-sm font-normal text-gray-700 mb-1">
              {t('discountModal.form.amount')}
            </label>
            <input type="number" name="amount" value={formData.amount} onChange={handleChange} className="w-full" />
          </div>
        )}

        {type === 'Percentage' && (
          <div>
            <label htmlFor="percentage" className="block text-sm font-normal text-gray-700 mb-1">
              {t('discountModal.form.percentage')}
            </label>
            <input type="number" name="percentage" value={formData.percentage} onChange={handleChange} className="w-full" />
          </div>
        )}

        <div>
          <label htmlFor="effectiveStartDate" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.startDate')}
          </label>
          <input type="date" name="effectiveStartDate" value={formData.effectiveStartDate} onChange={handleChange} className="w-full" />
        </div>

        <div>
          <label htmlFor="effectiveEndDate" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.expireDate')}
          </label>
          <input type="date" name="effectiveEndDate" value={formData.effectiveEndDate} onChange={handleChange} className="w-full" />
        </div>
        <div>
          <label htmlFor="discountValid" className="block text-sm font-normal text-gray-700 mb-1">
            {t('discountModal.form.discountValid')}
          </label>
          <input type="number" name="discountValid" value={formData.discountValid} onChange={handleChange} className="w-full" />
        </div>

        <div className="flex items-center space-x-4 pl-5">
          <p className="whitespace-nowrap">{t('discountModal.form.status')}</p>
          <Switch
            id="status"
            value={formData.isActive}
            onChange={checked => {
              setFormData(prev => ({
                ...prev,
                isActive: checked,
              }))
            }}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-600 hover:bg-dark-700"
          onClick={handleSaveDiscount}
        >
          {id ? t('discountModal.buttons.updateDiscount') : t('discountModal.buttons.addDiscount')}
        </button>

        <button type="button" className="ml-2 inline-flex justify-center rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200" onClick={close}>
          {t('discountModal.buttons.close')}
        </button>
      </div>
    </div>
  )
}

export default Discount
